import { useEffect, useState } from 'react';
import { MicButton, appendSpoken } from '@/components/ui/MicButton';
import { Phone, Loader2, X } from 'lucide-react';

/**
 * What is this call about? Asked before the line is opened.
 *
 * Before dialling rather than after, because that is the only moment the agent is certainly at the
 * screen — once the call connects they are talking, and once it ends they have moved on. The remark
 * is stored against the call, not the shipment: the same number is rung several times for different
 * reasons, and one remark per AWB would keep only the last of them. It can still be corrected
 * afterwards from the IVR list.
 *
 * Optional on purpose: a blocked field would be filled with "." within a week, and a required note
 * that nobody means is worse than no note.
 */
export function CallRemarksDialog({ open, toNumber, awbNo, busy, onCancel, onConfirm }: {
  open: boolean;
  toNumber: string;
  awbNo?: string | null;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (remarks: string) => void;
}) {
  const [remarks, setRemarks] = useState('');
  useEffect(() => { if (open) setRemarks(''); }, [open]);
  if (!open) return null;

  const go = () => onConfirm(remarks.trim());

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Phone size={16} className="text-emerald-600" /> Call {toNumber}</h3>
            {awbNo && <p className="text-xs text-slate-500 font-mono">{awbNo}</p>}
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5">
          <label className="block text-xs font-medium text-slate-600 mb-1">What is this call about? (optional)</label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={2000}
            rows={3}
            autoFocus
            placeholder="e.g. customer asked to redeliver on Saturday"
            className="input w-full resize-none"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) go(); }}
          />
          <div className="mt-1 flex justify-end"><MicButton onText={(t) => setRemarks((p: string) => appendSpoken(p, t).slice(0, 2000))} /></div>
          <div className="text-[11px] text-slate-400 mt-1">Saved against this call. You can correct it afterwards from the IVR screen.</div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={go} disabled={busy}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />} Place call
          </button>
        </div>
      </div>
    </div>
  );
}
